// =============================================================
// ŞAFAK · motion leaf — sinematik hero giriş + parallax
// Yeni konsept: Ken Burns sahne zoom'u, kapı kartı yükselişi,
// hayalet numara belirişi. Reduced-motion: her şey anında görünür.
// =============================================================

import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import { usePrefersReducedMotion } from "@/lib/animation/prefers-reduced-motion";
import { DUR, EASE } from "@/features/landing/motion/tokens";

export function useSafakMotion(scope: React.RefObject<HTMLElement | null>) {
  const reduced = usePrefersReducedMotion();

  useGSAP(
    () => {
      const q = (sel: string) => scope.current?.querySelector<HTMLElement>(sel) ?? null;
      const stage = q(".safak__stage img");
      const ghost = q(".safak__ghost");
      const panel = q(".safak__panel");
      const credit = q(".safak__credit");
      const cue = q(".safak__scroll-cue");
      const eyebrow = q(".safak__eyebrow");
      const titleEl = q(".safak__title");
      const sub = q(".safak__sub");
      const actions = q(".safak__actions");
      const meta = q(".safak__meta");
      const words = scope.current?.querySelectorAll<HTMLElement>(".safak__title .word");

      if (reduced) {
        gsap.set([stage, ghost, panel, credit, cue, eyebrow, sub, actions, meta, ...(words ?? [])], { clearProps: "all" });
        return;
      }

      // Ken Burns — sahne açılışta yavaşça yerine oturur
      if (stage) {
        gsap.fromTo(stage, { scale: 1.14 }, {
          scale: 1, duration: 2.6, ease: "power2.out",
        });
        // Subtle parallax — kademeli derinlik hissi
        gsap.to(stage, {
          yPercent: 8,
          ease: "none",
          scrollTrigger: { trigger: scope.current, start: "top top", end: "bottom top", scrub: 1.2 },
        });
      }

      // Kapı kartı — alçaktan yükselir
      if (panel) {
        gsap.fromTo(panel, { autoAlpha: 0, y: 56 }, {
          autoAlpha: 1, y: 0, duration: DUR.hero, ease: "expo.out", delay: 0.25,
        });
      }

      // Hayalet numara — sessizce belirir
      if (ghost) {
        gsap.fromTo(ghost, { autoAlpha: 0 }, {
          autoAlpha: 1, duration: 2.2, ease: "power1.out", delay: 0.9,
        });
      }

      // Kredi + scroll cue
      const tl = gsap.timeline({ delay: 1.1 });
      if (credit) tl.fromTo(credit, { autoAlpha: 0 }, { autoAlpha: 1, duration: DUR.slow, ease: EASE.out }, 0);
      if (cue) tl.fromTo(cue, { autoAlpha: 0, y: 12 }, { autoAlpha: 1, y: 0, duration: DUR.slow, ease: EASE.out }, 0.15);

      // Eyebrow fade-in
      if (eyebrow) {
        gsap.fromTo(eyebrow, { autoAlpha: 0, x: -16 }, {
          autoAlpha: 1, x: 0, duration: DUR.normal, ease: EASE.out, delay: 0.4,
        });
      }

      // Title kinetic reveal — per-word
      if (words && words.length) {
        gsap.fromTo(words, { autoAlpha: 0, yPercent: 120 }, {
          autoAlpha: 1, yPercent: 0,
          duration: DUR.slow,
          stagger: 0.05,
          ease: "expo.out",
          delay: 0.5,
        });
      } else if (titleEl) {
        gsap.fromTo(titleEl, { autoAlpha: 0, y: 32 }, {
          autoAlpha: 1, y: 0, duration: DUR.hero, ease: "expo.out", delay: 0.5,
        });
      }

      // Sub + actions + meta staggered after title
      const tl2 = gsap.timeline({ delay: 0.85 });
      if (sub)      tl2.fromTo(sub, { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: DUR.normal, ease: EASE.premium }, 0);
      if (actions)   tl2.fromTo(actions, { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: DUR.normal, ease: EASE.premium }, 0.1);
      if (meta)     tl2.fromTo(meta, { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: DUR.normal, ease: EASE.premium }, 0.2);
    },
    { scope, dependencies: [reduced] }
  );
}
